/**
 * The clinician's stable home hospital. Used as the active hospital until a
 * hospital switcher ships (FOOD-559).
 *
 * MNT dual-writes hospital_id on create and leaves it unchanged when a second
 * hospital is added, so that column is the home hospital by definition.
 * hospital_ids arrives newest-first, so the home membership is its last entry,
 * not its first. Reading the first entry pointed multi-hospital clinicians at
 * their most recently added hospital, which hides the home-hospital clinics
 * and patients now that MNT scopes both to the hospital in the request path.
 */
export function clinicianHomeHospitalId(clinician: {
  hospital_ids?: number[] | null;
  hospital_id?: number | null;
} | null | undefined): number | undefined {
  if (!clinician) {
    return undefined;
  }
  if (typeof clinician.hospital_id === 'number') {
    return clinician.hospital_id;
  }
  if (Array.isArray(clinician.hospital_ids) && clinician.hospital_ids.length > 0) {
    return clinician.hospital_ids[clinician.hospital_ids.length - 1];
  }
  return undefined;
}

/**
 * The hospital a per-patient, hospital-scoped call should target: the hospital
 * of the PATIENT's clinic, not the clinician's home hospital. A multi-hospital
 * clinician can view a patient whose clinic is in a non-home hospital; sending
 * the home hospital_id (resend invite, deregister, meal-logs, custom fields)
 * would hit the wrong hospital.
 *
 * Patient rows only carry `{id, name}` per clinic, so we look the clinic up in
 * the clinician's cross-hospital clinics (FOOD-837), which carry `hospital_id`.
 * Falls back to the home hospital when the clinic can't be resolved.
 */
export function patientHospitalId(
  clinician:
    | {
        hospital_ids?: number[] | null;
        hospital_id?: number | null;
        clinics?: Array<{ id?: number; hospital_id?: number } | null> | null;
      }
    | null
    | undefined,
  patient: { clinics?: Array<{ id?: number } | null> | null } | null | undefined,
): number | undefined {
  const clinicId = patient?.clinics?.[0]?.id;
  if (clinicId != null && Array.isArray(clinician?.clinics)) {
    const match = clinician!.clinics.find((c) => c?.id === clinicId);
    if (typeof match?.hospital_id === 'number') {
      return match.hospital_id;
    }
  }
  return clinicianHomeHospitalId(clinician);
}
